import { useParams } from 'react-router-dom';
import {
    customerSalesIcon,
    geographyIcon,
    globeGray,
    infoBlue,
    infoPurpleIcon,
    locationPinGray,
    plusBlack,
    productOfferingIcon,
    vendorsIcon,
} from '../../../../../../assets/hostedassets';
import Typewriter from '../../../../../../common/Typewriter/Typewriter';
import {
    AICA_OVERVIEW_SEE_MORE_CLICKED,
    INVESTOR_OPEN_COMPANY_WEBSITE,
} from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { getTimeStamp } from '../../../../../../utils/dateUtils';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { ensureHttpsPrefix, isEmpty, resolvePrimarySecondaryNames } from '../../../../../../utils/utils';
import styles from './Details.module.scss';
import { useCallback, useEffect, useState } from 'react';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { useIsMobile } from '../../../../../../common/_custom/Hooks/useIsMobile';
import AicaOverview from '../../../../../../common/AicaOverview/AicaOverview';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { setDealDrawer } from '../../../../../../store/investor/action';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../../../app/hooks';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { getFormerEntityData } from '../../../../../../store/investor/apiHelpers';
import { getDisplayEmployeeCount } from '../../../../../../store/dashboard/overview/utils';
import dayjs from 'dayjs';
import { findSubjectCompanyWebsite, pickWebsite } from '../../../../../../utils/urlMetadata';
import CompanyLogoBadge from '../../../../../../common/_custom/CompanyLogoBadge';
import { stripHtml, splitBullets } from '../../../../../AicaReportV2/data/overviewHelpers';
import ExpandableText from '../../../../../../common/ExpandableText/ExpandableText';
import AlertEngine from '../../../../components/AlertEngine/AlertEngine';
import CompaniesNetworkButton from '../../../../components/CompaniesNetwork/CompaniesNetworkButton';
import BasicDetailsSidebar from '../BasicDetailsSidebar/BasicDetailsSidebar';
import SocialsRow from './SocialsRow';
import { Social } from './socialPlatforms';
import MetricsKPISidebar from '../MetricsKPISidebar/MetricsKPISidebar';
import ScreenerCard from '../ScreenerCard/ScreenerCard';
import AICAGradeV2 from '../AICAGradeV2/AICAGradeV2';
import { aicaBg1, aicaBg4, aicaBgDefault } from '../../../../../../assets/hostedassets';
import { ACCESS_TIERS } from '../../../../../../store/investor/constants';
import UnlockOverviewSection from '../UnlockOverviewSection/UnlockOverviewSection';
import { useVisibilityConfig, useFeatureVisibility } from '../../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../../../utils/constants/visibilityKeys';
import { KEYBOARD_EVENTS, TabModule } from '../../../../../../utils/constants/commonConstants';

interface FormerEntity {
    name: string;
    date: string;
}

const OverviewBulletsSection = ({ content, selectedCompany }: { content: string; selectedCompany?: any }) => (
    <ExpandableText
        content={content}
        onExpand={() =>
            Mixpanel.track(AICA_OVERVIEW_SEE_MORE_CLICKED, {
                Timestamp: getTimeStamp(),
                companyName: selectedCompany?.orgName,
                AICASection: selectedCompany?.companyAccessType,
            })
        }
    />
);

function Details({ overviewData, accessType, selectedCompany, isPDFReport, overviewDataV2, leftSlot, rightSlot, navigateToTab }) {
    const {
        sectorInfo,
        overview,
        businessModel,
        socialInfo,
        foundationDate,
        headquarterLocation,
        fundingInfo,
        employeeData,
        cinNumber,
        panNumber,
        gstin,
    } = overviewData;

    // RECUR-21135 R3/R7: brand name (displayName), empty-safe fallback to legal orgName. The
    // company-name matches against AI-generated data below (findSubjectCompanyWebsite) intentionally
    // keep using the legal orgName — that lookup matches against the AI overview's own "Target
    // company" entry, which is keyed off the legal name, not the brand.
    const { primary: companyDisplayName } = resolvePrimarySecondaryNames(selectedCompany?.displayName, selectedCompany?.orgName);
    const hasBrandName = !!selectedCompany?.displayOrgName?.trim();

    const params = useParams();
    const [formerEntityData, setFormerEntityData] = useState<FormerEntity[]>([]);
    const [forceHideFormer, setForceHideFormer] = useState(false);
    const user = useAppSelector((state) => state.user.userData);
    const askAicaConfig = useAppSelector((state) => state.dashboard.dashboardReducer.config?.tabConfigs?.overview?.showAskAica);
    // On mobile, portal the header pill tooltips so they escape overflow clipping and
    // get viewport-edge clamping (otherwise they run off the small screen).
    const isMobile = useIsMobile();
    // Desktop keeps its original truthy gate, untouched. Mobile uses the SAME gate as
    // MobileAskAicaOrb ("hidden only when the tab config explicitly disables Ask AICA"),
    // because the two are one feature: with the config key simply absent, the strict
    // truthy check hid this bar while the orb still showed — Ask AICA reachable from the
    // floating orb but not from the company card. RECUR-27903.
    const showAskAica = isMobile ? askAicaConfig !== false : !!askAicaConfig;
    // This bar owns no Ask AICA surface — it forwards the tap to whichever entry point
    // does. Desktop renders AskAicaV2's GlobalIcon; mobile renders no GlobalIcon at all
    // (MobileAskAicaOrb owns the full-screen mobile surface), so the GlobalIcon lookup
    // found nothing and the bar was inert on mobile. RECUR-27903.
    const openAskAica = useCallback(() => {
        const entryPoint = isMobile
            ? (document.querySelector('[data-testid="mobile-askaica-orb"]') as HTMLElement | null)
            : (document.querySelector('[class*="GlobalIcon"]') as HTMLElement | null);
        if (!entryPoint) {
            // Selector-based lookup across component boundaries — if the target markup changes
            // (data-testid renamed, CSS-module hash shifts), this bar silently goes inert with no
            // error. Surface it instead of failing silently, per review on RECUR-27903.
            console.error('[Ask AICA] entry point not found via selector — the company card bar will be inert.', {
                isMobile,
            });
            return;
        }
        entryPoint.click();
    }, [isMobile]);
    const isValidUser = [USER_TYPE.INVESTOR, USER_TYPE.ADMIN_TEAM].includes(user?.accountType);
    const dispatch = useDispatch();
    // The company's own website often lives only in the AI overview's competitor
    // analysis — the report tags the company itself as the "Target company" entry
    // (a.k.a. subject company). Key Competitors renders its logo from exactly this,
    // so read the same source here for parity when socialInfo has no website.
    // pickWebsite skips sentinels like "NA" (socialInfo/selectedCompany frequently
    // send "NA"), so the real subject-company website wins instead of a junk favicon.
    const { config: visibilityConfig } = useVisibilityConfig();
    const { sidebarRelatedNetworkVisibility } = useFeatureVisibility({
        sidebarRelatedNetworkVisibility: VISIBILITY_KEYS.SIDEBAR.RELATED_NETWORK_ENABLE,
    });
    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus);
    const isRelatedPartyLocked = !!(tabLockStatus as any)?.[TabModule.RELATED_PARTY]?.isLocked;

    const additionalDetails = (cinNumber && cinNumber !== '') || (panNumber && panNumber !== '') || gstin?.length > 0;
    const bodyHasData = !!(
        sectorInfo?.sector ||
        sectorInfo?.subSector ||
        !!overview ||
        !!businessModel ||
        !!headquarterLocation?.fullAddress ||
        additionalDetails ||
        (!isPDFReport && overviewDataV2?.screenerData)
    );

    useEffect(() => {
        fetchFormerEntityData();
    }, [params.id]);

    const fetchFormerEntityData = () => {
        getFormerEntityData(
            { organizationId: params.id || '' },
            {
                onSuccess: (data: FormerEntity[]) => {
                    setFormerEntityData(data);
                },
            },
        );
    };

    const onWebsiteClick = () => {
        Mixpanel.track(INVESTOR_OPEN_COMPANY_WEBSITE, {
            Timestamp: getTimeStamp(),
            AICASection: accessType,
        });
    };

    function formatDate(inputDate: string): string {
        let date: Date;
        if (inputDate.includes('T')) {
            // ISO format: "2018-02-20T00:00:00+05:30"
            date = new Date(inputDate);
        } else {
            // DD-MM-YYYY format
            const [day, month, year] = inputDate.split('-');
            date = new Date(`${year}-${month}-${day}`);
        }

        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        };
        return date.toLocaleDateString('en-GB', options).replace(/ /g, ' ');
    }

    const getFormerEntityTooltipContent = (formerEntities: FormerEntity[]) => {
        return (
            <div>
                {formerEntities?.map((entity, index) => (
                    <div className={styles.FormerEntityTooltip} key={`${entity.name}-${index}`}>
                        <div className={styles.FormerEntityName}>
                            <span>{entity.name}</span>
                        </div>
                        {entity.date && <div className={styles.FormerEntityDate}>(Till {dayjs(entity.date).format('DD MMM YYYY')})</div>}
                        {index !== formerEntities.length - 1 && <div className={styles.FormerEntitySeparator}></div>}
                    </div>
                ))}
            </div>
        );
    };

    const openEditProfileDrawer = () => {
        dispatch(
            setDealDrawer({
                open: true,
                drawerLabel: 'Edit Profile',
                drawerSource: 'Debt Products',
                selectedStep: 2,
            }),
        );
    };

    const OVERVIEW_BGS = [aicaBg1, aicaBg4, aicaBgDefault];
    const bgIndex = (params.id || '').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % OVERVIEW_BGS.length;

    const renderTopSection = () => (
        <div className={styles.TopWrapper}>
            <div
                className={clsx(styles.Backdrop, { [styles.BackdropPDF]: isPDFReport })}
                style={!isPDFReport ? { backgroundImage: `url(${OVERVIEW_BGS[bgIndex]})` } : undefined}
            />
            <div className={styles.TopGrid}>
                <div className={styles.CompanyCard}>
                    <div className={styles.CardInner}>
                        <div className={styles.CardTopRow}>
                            <div className={styles.CompanyLogo}>
                                <CompanyLogoBadge
                                    name={selectedCompany?.orgName ?? companyDisplayName}
                                    logo={selectedCompany?.logo}
                                    websiteUrl={pickWebsite(
                                        socialInfo?.websiteUrl,
                                        findSubjectCompanyWebsite(overviewDataV2?.competitors, selectedCompany?.orgName),
                                        selectedCompany?.websiteUrl,
                                    )}
                                    size={72}
                                    shape="square"
                                    className={styles.CardLogoBadge}
                                />
                            </div>
                            <div className={styles.CompanyInfo}>
                                {/* RECUR-21135 R7: Registered name (orgName) as the title. The brand now has
                                    its own "Brand Name" header pill (see renderCompanyDetails below), so the
                                    title attr no longer needs to carry both names. */}
                                <div className={styles.Title} title={selectedCompany?.orgName}>
                                    <Typewriter text={selectedCompany?.orgName ?? companyDisplayName} delay={20} />
                                </div>
                                <div className={styles.MetaRow}>
                                    {!!(headquarterLocation?.city || headquarterLocation?.state) && (
                                        <div className={styles.MetaItem}>
                                            <img src={locationPinGray} alt="" className={styles.LocationIcon} />
                                            <span>
                                                {headquarterLocation?.city}
                                                {headquarterLocation?.city && headquarterLocation?.state && ', '}
                                                {headquarterLocation?.state}
                                            </span>
                                        </div>
                                    )}
                                    {!!socialInfo?.websiteUrl ? (
                                        <a
                                            className={clsx(styles.MetaItem, styles.MetaItemLink)}
                                            href={ensureHttpsPrefix(socialInfo.websiteUrl)}
                                            target="_blank"
                                            onClick={onWebsiteClick}
                                        >
                                            <img src={globeGray} alt="" className={styles.GlobeIcon} />
                                            <span>{socialInfo.websiteUrl.replace(/^https?:\/\//, '')}</span>
                                        </a>
                                    ) : (
                                        isValidUser && (
                                            <div className={clsx(styles.MetaItem, styles.MetaItemLink)} onClick={openEditProfileDrawer}>
                                                <img src={plusBlack} alt="" className={styles.AddIcon} />
                                                <span>Add Website</span>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                            {!isPDFReport && (
                                <SocialsRow
                                    socials={overviewDataV2?.overview?.socials as Social[] | undefined}
                                    isValidUser={isValidUser}
                                    onAddLinkedin={openEditProfileDrawer}
                                    userInputLinkedin={socialInfo?.userInputLinkedin}
                                />
                            )}
                        </div>

                        {!isPDFReport && showAskAica && (
                            <div
                                className={styles.AskAicaInput}
                                onClick={openAskAica}
                                {...(isMobile
                                    ? {
                                          role: 'button',
                                          tabIndex: 0,
                                          'aria-label': 'Ask anything',
                                          onKeyDown: (e: React.KeyboardEvent) => {
                                              if (e.key === KEYBOARD_EVENTS.ENTER || e.key === KEYBOARD_EVENTS.SPACE) {
                                                  e.preventDefault();
                                                  openAskAica();
                                              }
                                          },
                                      }
                                    : {})}
                            >
                                <span className={styles.AskAicaPlaceholder}>Ask anything</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Hidden on mobile for now — the Alerts/Connection drawer isn't handled on
                    mobile yet (planned for the second release). Desktop unchanged. */}
                {!isPDFReport && !isMobile && (
                    <div className={styles.ActivityCards}>
                        <AlertEngine selectedCompany={selectedCompany} currentTab="overview" variant="card" />
                        {visibilityConfig && sidebarRelatedNetworkVisibility.show && (
                            <CompaniesNetworkButton
                                companyName={selectedCompany?.orgName}
                                isRelatedPartiesLocked={isRelatedPartyLocked}
                                variant="card"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    const isOverviewLocked = overviewData?.overallStatus === 'LOCKED';

    const aiCards = [
        {
            key: 'productOfferings',
            title: 'Product/ Service Offering:',
            content: overviewDataV2?.overview?.productOfferings,
            icon: productOfferingIcon,
        },
        {
            key: 'customersAndSalesChannels',
            title: 'Customer Sales & Channels',
            content: overviewDataV2?.overview?.customersAndSalesChannels,
            icon: customerSalesIcon,
        },
        {
            key: 'vendorsSourcingManufacturing',
            title: 'Vendors, Sourcing & Manufacturing',
            content: overviewDataV2?.overview?.vendorsSourcingManufacturing,
            icon: vendorsIcon,
        },
        {
            key: 'geographiesSegmentation',
            title: 'Geographies / Segmentation',
            content: overviewDataV2?.overview?.geographiesSegmentation,
            icon: geographyIcon,
        },
    ].filter((card) => !!card.content);

    const tradeNames: string[] = overviewDataV2?.tradeNames ?? [];
    const gstTurnover =
        overviewDataV2?.latestFy && overviewDataV2?.latestFyTurnoverBand
            ? { fy: overviewDataV2.latestFy, band: overviewDataV2.latestFyTurnoverBand }
            : null;

    const renderCompanyDetails = () => {
        const sectorValue = overviewDataV2?.sectorDetails?.sector || sectorInfo?.sector;
        const subSectorValue = overviewDataV2?.sectorDetails?.subSector || sectorInfo?.subSector;
        const sectorFieldLabel = sectorValue && subSectorValue ? 'Sector/Sub-Sector' : sectorValue ? 'Sector' : 'Sub-Sector';
        const sectorDisplayValue = [sectorValue, subSectorValue].filter(Boolean).join('/ ');
        const foundedValue = foundationDate ? formatDate(foundationDate) : null;
        const revenueValue = gstTurnover ? gstTurnover.band : overviewDataV2?.overview?.revenue;
        const keyInvestorsValue = overviewDataV2?.overview?.keyInvestors || (fundingInfo?.keyInvestors ? [fundingInfo.keyInvestors] : null);
        const fundingRaisedValue = overviewDataV2?.overview?.fundingRaised || (fundingInfo?.stage ?? null);
        const totalEmployeesValue = getDisplayEmployeeCount(employeeData);
        const overviewContent = overviewDataV2?.overview?.overviewContent || overview;

        const hasGridData =
            sectorValue ||
            subSectorValue ||
            foundedValue ||
            revenueValue ||
            keyInvestorsValue ||
            fundingRaisedValue ||
            totalEmployeesValue != null;
        const hasContent = hasGridData || overviewContent;
        // Equal-width + truncation on mobile once 2+ header pills share the row (Figma). Trade
        // Names, Brand Name, and Former Names can each independently show or not, so this counts
        // however many are actually visible rather than assuming exactly two.
        const visiblePillCount = [tradeNames.length > 0, hasBrandName, !isEmpty(formerEntityData)].filter(Boolean).length;
        const hasBothPills = !isPDFReport && visiblePillCount > 1;

        const getTradeNamesTooltipContent = () => (
            <div>
                {tradeNames.map((name, index) => (
                    <div className={styles.FormerEntityTooltip} key={`trade-${index}`}>
                        <div className={styles.FormerEntityName}>
                            <span>{name}</span>
                        </div>
                    </div>
                ))}
            </div>
        );

        // RECUR-21135: "Brand Name" now has its own header pill, independent of trade names —
        // this fixes the bug where, with no trade names, the brand had nowhere to show.
        const getBrandNameTooltipContent = () => (
            <div>
                <div className={styles.TradeNamesTooltipLabel}>Brand Name</div>
                <div className={styles.TradeNamesTooltipValue}>{companyDisplayName}</div>
            </div>
        );

        if (!hasContent && !isOverviewLocked) return null;

        return (
            <>
                <div className={styles.BottomWrap}>
                    <div className={styles.Header}>
                        <div className={styles.Heading}>Company Overview</div>
                        <div className={clsx(styles.Right, hasBothPills && styles.RightTwoPills)}>
                            {tradeNames.length > 0 && !isPDFReport && (
                                <div
                                    className={styles.HeaderPillWrap}
                                    onMouseEnter={() => setForceHideFormer(true)}
                                    onMouseLeave={() => setForceHideFormer(false)}
                                >
                                    <Tooltip
                                        content={getTradeNamesTooltipContent()}
                                        direction="bottom"
                                        hideDelay={500}
                                        isOverviewDetail={true}
                                        usePortal={true}
                                        triggerOnClick={isMobile}
                                        containerFullWidth={isMobile && hasBothPills}
                                        tipClassName={styles.HeaderPillTooltipWidth}
                                    >
                                        <div className={styles.HeaderPill}>
                                            <span className={styles.HeaderPillLabel}>
                                                {tradeNames.length} Trade {tradeNames.length === 1 ? 'Name' : 'Names'}
                                            </span>
                                            <img src={infoPurpleIcon} alt="" className={styles.HeaderPillIcon} />
                                        </div>
                                    </Tooltip>
                                </div>
                            )}
                            {hasBrandName && !isPDFReport && (
                                <Tooltip
                                    content={getBrandNameTooltipContent()}
                                    direction="bottom"
                                    hideDelay={500}
                                    isOverviewDetail={true}
                                    usePortal={true}
                                    triggerOnClick={isMobile}
                                    tipClassName={styles.HeaderPillTooltipWidth}
                                >
                                    <div className={clsx(styles.HeaderPill, styles.HeaderPillBrand)}>
                                        <span className={styles.HeaderPillLabel}>Brand Name</span>
                                        <img src={infoBlue} alt="" className={styles.HeaderPillIcon} />
                                    </div>
                                </Tooltip>
                            )}
                            {!isEmpty(formerEntityData) && (
                                <Tooltip
                                    content={forceHideFormer ? '' : getFormerEntityTooltipContent(formerEntityData)}
                                    direction="bottom"
                                    hideDelay={forceHideFormer ? 0 : 500}
                                    isOverviewDetail={true}
                                    usePortal={true}
                                    triggerOnClick={isMobile}
                                    containerFullWidth={isMobile && hasBothPills}
                                    tipClassName={styles.HeaderPillTooltipWidth}
                                    contentCss={{ maxHeight: '8rem', overflowY: 'auto' }}
                                >
                                    <div className={clsx(styles.HeaderPill, styles.HeaderPillFormer)}>
                                        <span className={styles.HeaderPillLabel}>
                                            {formerEntityData.length} Former {formerEntityData.length === 1 ? 'Name' : 'Names'}
                                        </span>
                                        <img src={infoBlue} alt="" className={styles.HeaderPillIcon} />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <div className={styles.Bottom}>
                        {isOverviewLocked ? (
                            <UnlockOverviewSection overviewData={overviewData} />
                        ) : (
                            <>
                                {hasGridData && (
                                    <div className={styles.DetailsGrid}>
                                        {sectorDisplayValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>{sectorFieldLabel}</span>
                                                <Tooltip
                                                    content={subSectorValue || sectorValue}
                                                    direction="top"
                                                    isOverviewDetail={true}
                                                    usePortal={true}
                                                    hideDelay={500}
                                                    containerFullWidth={true}
                                                >
                                                    <span className={clsx(styles.DetailValue, styles.DetailValueOneLine)}>
                                                        {sectorDisplayValue}
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        )}
                                        {foundedValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Founded</span>
                                                <span className={styles.DetailValue}>{foundedValue}</span>
                                            </div>
                                        )}
                                        {revenueValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>FY'24-25 Revenue</span>
                                                <span className={styles.DetailValue}>{revenueValue}</span>
                                            </div>
                                        )}
                                        {keyInvestorsValue && keyInvestorsValue.length > 0 && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Key Investors</span>
                                                <span className={styles.DetailValue}>
                                                    {keyInvestorsValue[0]}
                                                    {keyInvestorsValue.length > 1 && (
                                                        <Tooltip
                                                            content={keyInvestorsValue.slice(1).join(', ')}
                                                            direction="top"
                                                            isOverviewDetail={true}
                                                            fitContent={true}
                                                            usePortal={true}
                                                            hideDelay={500}
                                                        >
                                                            <span className={styles.MoreLink}> +{keyInvestorsValue.length - 1} more</span>
                                                        </Tooltip>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {fundingRaisedValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Funding Raised</span>
                                                <span className={styles.DetailValue}>{fundingRaisedValue}</span>
                                            </div>
                                        )}
                                        {totalEmployeesValue != null && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Total Employees</span>
                                                <span className={styles.DetailValue}>{totalEmployeesValue}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {overviewContent && <OverviewBulletsSection content={overviewContent} selectedCompany={selectedCompany} />}
                            </>
                        )}
                    </div>
                </div>

                {/* AI Business Model Cards — hidden when locked */}
                {!isOverviewLocked && aiCards.length > 0 && (
                    <div className={styles.AICardsGrid}>
                        {aiCards.map((card) => (
                            <div key={card.key} className={styles.AICard}>
                                <div className={styles.AICardTitle}>
                                    <img src={card.icon} alt="" width="20" height="20" />
                                    <span>{card.title}</span>
                                </div>
                                <ul className={styles.AICardContent}>
                                    {splitBullets(stripHtml(card.content)).map((bullet, i) => (
                                        <li key={i}>{bullet.replace(/^[•·\-–—]\s*/, '').trim()}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    const render = (
        <div className={styles.Details} style={{ position: 'relative' }}>
            {renderTopSection()}
            <AicaOverview
                AicaOverviewBodyStyle={{ border: '1px solid var(--border-color-new)' }}
                style={{ marginBottom: '1.5rem' }}
                module="overview"
            />
            {bodyHasData ? (
                isMobile ? (
                    <div className={styles.BentoGridMobile}>
                        {renderCompanyDetails()}
                        <MetricsKPISidebar orgId={params.id} navigateToTab={navigateToTab} isLocked={isOverviewLocked} />
                        {!isOverviewLocked && accessType !== ACCESS_TIERS.WATCH && <AICAGradeV2 isPDFReport={false} />}
                        {leftSlot}
                        {rightSlot}
                        <BasicDetailsSidebar
                            panNumber={panNumber}
                            cinNumber={cinNumber}
                            gstin={gstin}
                            headquarterLocation={headquarterLocation}
                        />
                    </div>
                ) : (
                    <div className={styles.BentoGrid}>
                        <div className={styles.BentoLeft}>
                            {renderCompanyDetails()}
                            {leftSlot}
                        </div>
                        {!isPDFReport && (
                            <div className={styles.BentoRight}>
                                {overviewDataV2?.screenerData && (
                                    <ScreenerCard screenerData={overviewDataV2.screenerData} generatedAt={overviewDataV2.generatedAt} />
                                )}
                                <MetricsKPISidebar orgId={params.id} navigateToTab={navigateToTab} isLocked={isOverviewLocked} />
                                {rightSlot}
                                {!isOverviewLocked && accessType !== ACCESS_TIERS.WATCH && <AICAGradeV2 isPDFReport={false} />}
                                <BasicDetailsSidebar
                                    panNumber={panNumber}
                                    cinNumber={cinNumber}
                                    gstin={gstin}
                                    headquarterLocation={headquarterLocation}
                                />
                            </div>
                        )}
                    </div>
                )
            ) : (
                !isPDFReport && !isOverviewLocked && accessType !== ACCESS_TIERS.WATCH && <AICAGradeV2 isPDFReport={false} />
            )}
        </div>
    );

    if (isPDFReport)
        return reportSectionsToShow?.includes(REPORT_SECTION_NAMES.OVERVIEW) ? (
            <>
                <A4Page sectionName={REPORT_SECTION_NAMES.OVERVIEW}>
                    <div className={clsx(styles.Details, styles.PDFReport)}>
                        {renderTopSection()}
                        <AicaOverview style={{ marginTop: '0px', marginBottom: '24px' }} module={'overview'} isPDFReport={isPDFReport} />
                        {/* <>{renderCompanyDetails()}</> */}
                    </div>
                </A4Page>
                {(bodyHasData || additionalDetails) && (
                    <A4Page sectionName={REPORT_SECTION_NAMES.OVERVIEW}>
                        <div className={clsx(styles.Details, styles.PDFReport)}>
                            {renderCompanyDetails()}
                            <BasicDetailsSidebar
                                panNumber={panNumber}
                                cinNumber={cinNumber}
                                gstin={gstin}
                                headquarterLocation={headquarterLocation}
                            />
                        </div>
                    </A4Page>
                )}
            </>
        ) : (
            <></>
        );
    else return render;
}

export default Details;
